import React, { useEffect, useState } from "react";
import { Quantum } from 'ldrs/react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  AreaChart,
  Area,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import DashboardFooter from "../components/DashboardFooter";

const DashboardReports = () => {
  const [dashboardReports, setDashboardReports] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [whatsappChartData, setWhatsappChartData] = useState([]);
  const [appointmentStatusPieData, setAppointmentStatusPieData] = useState([]);
  const [paymentBreakdownData, setPaymentBreakdownData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChats, setShowChats] = useState(false); // toggle for whatsapp chart view

  // Dynamic color assignment for any status
  const getStatusColor = (status) => {
    const statusColors = {
      'assigned': '#17a2b8',    // Blue
      'accepted': '#28a745',    // Green  
      'completed': '#6f42c1',   // Purple
      'pending': '#ffc107',     // Yellow
      'cancelled': '#dc3545',   // Red
      'rejected': '#fd7e14',    // Orange
      'in_progress': '#20c997', // Teal
      'scheduled': '#6c757d'    // Gray
    };
    return statusColors[status] || '#6c757d'; // Default gray for unknown statuses
  };

  const fetchDashboardReports = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_HOST_API_URL}/admin-user/dashboard-data-listing/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      setDashboardReports(data.data);

      // Chart 1: Appointments over last 7 days
      const counts = data.data.last_7_days_appointment_counts || {};
      const formattedData = Object.entries(counts).map(([date, count]) => {
        const d = new Date(date);
        const month = d.toLocaleString("default", { month: "short" });
        const day = d.getDate();
        return { date: `${month} ${day}`, appointments: count };
      });
      setChartData(formattedData);

      // Chart 2: WhatsApp Data
      const whatsappData = data.data.whatsapp_data || [];
      const formattedWhatsapp = whatsappData.map((item) => {
        const d = new Date(item.date);
        const month = d.toLocaleString("default", { month: "short" });
        const day = d.getDate();
        return {
          date: `${month} ${day}`,
          created: item.created_over_whatsapp,
          appointments: item.whatsapp_appointments,
          chats: item.chat_over_whatsapp,
        };
      });
      setWhatsappChartData(formattedWhatsapp);

      // Chart 3: Appointment Status Pie Chart
      const appointmentStatusData = data.data.today_cleaning_appoinements_by_status || {};
      const pieData = Object.entries(appointmentStatusData).map(([status, count]) => ({
        name: status.charAt(0).toUpperCase() + status.slice(1),
        value: count || 0,
        status: status.toLowerCase()
      }));
      setAppointmentStatusPieData(pieData);

      // Chart 4: Payment Breakdown Data
      const paymentBreakdown = data.data.payment_breakdown || {};
      const breakdownData = Object.entries(paymentBreakdown).map(([type, amount]) => ({
        name: type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        value: amount || 0,
        amount: amount || 0
      }));
      setPaymentBreakdownData(breakdownData);
    } catch (err) {
      console.log("Error fetching dashboard data:", err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardReports();
    const interval = setInterval(fetchDashboardReports, 10000); // refresh every 10 sec
    return () => clearInterval(interval);
  }, []);

  // if (loading) {
  //   return (
  //     <div
  //       className="d-flex justify-content-center align-items-center"
  //       style={{ height: "60vh" }}
  //     >
  //       <h5>Loading dashboard data...</h5>
  //     </div>
  //   );
  // }
    if (loading) return (
    <>
      <div className="d-flex justify-content-center align-items-center " style={{
        minHeight: 'calc(100vh - 40px)',
        width: '100vw',
        marginLeft: '-280px',
        padding: '20px'
      }}>
        <Quantum
          size="45"
          speed="1.75"
          color="blue"
        />
      </div>
    </>
  );


  return (
     <div className="d-flex flex-column min-vh-100 pb-2 px-3">
    {/* Main content area (scrolls normally) */}
    <div className="flex-grow-1">
    <motion.div
      className="container-fluid px-2 px-md-4"
      style={{ paddingTop: "20px" }}
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -30 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      {/* Header Section */}
      <motion.div
        className="row mb-4 bg-white shadow-sm border-bottom sticky-top py-4 px-md-3 px-2 rounded"
        style={{
          zIndex: 1050,
          background: "linear-gradient(135deg, #0564bf, #89CFF0)",
        }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <div className="col-12 d-flex justify-content-between align-items-center">
          <div>
            <h3 className="text-light mb-1">Dashboard Reports</h3>
            <p className="text-light mb-0">
              View and track all dashboard reports within the system.
            </p>
          </div>
          <button
            className="btn btn-light btn-sm fw-semibold"
            onClick={() => setShowChats((prev) => !prev)}
          >
            {showChats
              ? "Show WhatsApp Appointments"
              : "Show Chats Over WhatsApp"}
          </button>
        </div>
      </motion.div>

      <motion.div className="text-center mt-4 mb-2">
        <small className="text-muted">
          Data refreshes every 10 seconds to ensure real-time accuracy.
        </small>
      </motion.div>  

      {/* Charts Section */}
      <div className="d-flex justify-content-between align-items-start gap-5 flex-wrap">
        {/* 1️⃣ Appointments Chart */}
        <motion.div
          className="w-50"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <div className="shadow-sm p-3 bg-white rounded">
            <h5 className="text-center mb-3">Appointments Analytics</h5>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="appointments"
                  stroke="#28a745"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* 2️⃣ WhatsApp Line Chart (Toggle Between Chats or Appointments) */}
        <motion.div
          className="flex-grow-1"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
        >
          <div className="shadow-sm p-3 bg-white rounded">
            <h5 className="text-center mb-3">
              {showChats
                ? "Chats Over WhatsApp (Last 10 Days)"
                : "WhatsApp Appointments & Created (Last 10 Days)"}
            </h5>

            <AnimatePresence mode="wait">
              <motion.div
                key={showChats ? "chats" : "appointments"}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={whatsappChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />

                    
                    {/* Show created_over_whatsapp only when displaying WhatsApp Appointments */}
                    {!showChats && (
                      <Line
                        type="monotone"
                        dataKey="created"
                        stroke="#007bff"
                        strokeWidth={3}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                        name="New Appointments Created"
                      />
                    )}

                    {/* Toggle between chats and appointments */}
                    <Line
                      type="monotone"
                      dataKey={showChats ? "chats" : "appointments"}
                      stroke={showChats ? "#ffc107" : "#17a2b8"}
                      strokeWidth={3}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                      name={
                        showChats
                          ? "Messages count Over WhatsApp"
                          : "WhatsApp Appointments Booked"
                      }
                    />
                  </LineChart>
                </ResponsiveContainer>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </div>

      {/* Today's Cleaning Appointments by Status - Pie Chart */}
      <motion.div 
        className="mt-3 p-4 bg-white "
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.6 }}
      >
        <h4 className="text-center text-muted mb-4">Today's Cleaning Appointments by Status</h4>
        
        <div className="row align-items-center">
          <div className="col-md-6">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={appointmentStatusPieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {appointmentStatusPieData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={getStatusColor(entry.status)}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div className="col-md-6">
            <div className="d-flex flex-column gap-3">
              {appointmentStatusPieData.map((entry, index) => (
                <div key={entry.name} className="d-flex align-items-center">
                  <div 
                    className="me-3 rounded-circle" 
                    style={{ 
                      width: "20px", 
                      height: "20px",
                      backgroundColor: getStatusColor(entry.status)
                    }}
                  ></div>
                  <div className="flex-grow-1">
                    <span className="fw-semibold">{entry.name} Appointments</span>
                    <span className="float-end fw-bold text-muted">{entry.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>


      {/* Stats Cards Section */}
      <motion.div 
        className="d-flex justify-content-center my-4 gap-3 flex-wrap shadow-sm rounded py-5 "
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.5 }}
        style={{
          background: "linear-gradient(135deg, #ffffff, #89CFF0)",
        }}
      >
        {/* WhatsApp Pending Appointments */}

        <div className="card shadow-sm text-center p-3 bg-white rounded" style={{ minWidth: "180px" }}>
          <h6 className="text-muted mb-2">Pending WhatsApp Appointments</h6>

          <div className="d-flex justify-content-center align-items-center">
        <img src="https://img.icons8.com/?size=100&id=64004&format=png&color=000000" alt="active employees" />
        <p className={`mb-0 display-3 fw-bold ${(dashboardReports?.whatsapp_pending_appointments || 0) > 0 ? 'text-warning' : 'text-success'}`}>
          
            {dashboardReports?.whatsapp_pending_appointments || 0}
          </p>

          </div>
        </div>

        {/* Today's Website Visits */}

        <div className="card shadow-sm text-center p-3 bg-white rounded" style={{ minWidth: "180px" }}>
          <h6 className="text-muted mb-2">Today's Website Visits</h6>

          <div className="d-flex justify-content-center align-items-center">
        <img src="https://img.icons8.com/?size=100&id=122521&format=png&color=000000" alt="active employees" />
        <p className={`mb-0 display-3 fw-bold ${(dashboardReports?.user_visit_over_website || 0) > 0 ? 'text-success' : 'text-muted'}`}>
          
            {dashboardReports?.user_visit_over_website || 0}
          </p>

          </div>
        </div>



        {/* Today's Active Employees */}
        <div className="card shadow-sm  text-center p-3 bg-white rounded" style={{ minWidth: "180px" }}>
          <h6 className="text-muted mb-2">Today's Active Employees</h6>

          <div className="d-flex justify-content-center align-items-center">
        <img src="https://img.icons8.com/?size=100&id=64172&format=png&color=000000" alt="active employees" />
        <p className={`mb-0 display-3 fw-bold ${(dashboardReports?.today_active_employees_count || 0) > 0 ? 'text-success' : 'text-warning'}`}>
          
            {dashboardReports?.today_active_employees_count || 0}
          </p>

          </div>
        </div>
      </motion.div>


      {/* Today's Payment Data Section */}
      <motion.div 
        className="d-flex justify-content-center my-4 gap-3 flex-wrap  py-5"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.7 }}
      >
        <div className="text-center w-100 mb-3">
          <h4 className="text-muted mb-0">Today's Payment Analytics</h4>
          <small className="text-muted">Real-time payment status tracking</small>
        </div>

        {/* Today's Payments Done */}
        <div className="card shadow-sm text-center p-4 bg-white rounded position-relative overflow-hidden" style={{ minWidth: "200px" }}>
          <div className="position-absolute top-0 end-0 p-2">
            <div className="bg-success bg-opacity-10 rounded-circle p-2">
              <img src="https://img.icons8.com/?size=100&id=59805&format=png&color=28a745" alt="payments done" style={{ width: "24px", height: "24px" }} />
            </div>
          </div>
          <h6 className="text-muted mb-3 mt-2">Payments Completed</h6>
          <div className="d-flex justify-content-center align-items-center">
            <p className="mb-0 display-4 fw-bold text-success">
              {dashboardReports?.today_payment_data?.today_payment_done_count || 0}
            </p>
          </div>
          <small className="text-success fw-semibold">✓ Successfully processed</small>
        </div>

        {/* Today's Payments Failed */}
        <div className="card shadow-sm text-center p-4 bg-white rounded position-relative overflow-hidden" style={{ minWidth: "200px" }}>
          <div className="position-absolute top-0 end-0 p-2">
            <div className="bg-danger bg-opacity-10 rounded-circle p-2">
              <img src="https://img.icons8.com/?size=100&id=59805&format=png&color=dc3545" alt="payments failed" style={{ width: "24px", height: "24px" }} />
            </div>
          </div>
          <h6 className="text-muted mb-3 mt-2">Payments Failed</h6>
          <div className="d-flex justify-content-center align-items-center">
            <p className="mb-0 display-4 fw-bold text-danger">
              {dashboardReports?.today_payment_data?.today_payment_failed_count || 0}
            </p>
          </div>
          <small className="text-danger fw-semibold">⚠ Requires attention</small>
        </div>

        {/* Today's Payments Pending */}
        <div className="card shadow-sm text-center p-4 bg-white rounded position-relative overflow-hidden" style={{ minWidth: "200px" }}>
          <div className="position-absolute top-0 end-0 p-2">
            <div className="bg-warning bg-opacity-10 rounded-circle p-2">
              <img src="https://img.icons8.com/?size=100&id=59805&format=png&color=ffc107" alt="payments pending" style={{ width: "24px", height: "24px" }} />
            </div>
          </div>
          <h6 className="text-muted mb-3 mt-2">Payments Pending</h6>
          <div className="d-flex justify-content-center align-items-center">
            <p className="mb-0 display-4 fw-bold text-warning">
              {dashboardReports?.today_payment_data?.today_payment_pending_count || 0}
            </p>
          </div>
          <small className="text-warning fw-semibold">⏳ In progress</small>
        </div>
      </motion.div>

      {/* All Time Payment Data Section */}
      <motion.div 
        className="d-flex justify-content-center my-4 gap-3 flex-wrap shadow-sm rounded py-5"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.8 }}
        style={{
          background: `
            radial-gradient(circle at 25% 25%, rgba(5, 100, 191, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, rgba(137, 207, 240, 0.1) 0%, transparent 50%),
            linear-gradient(45deg, #f8f9fa 25%, transparent 25%),
            linear-gradient(-45deg, #f8f9fa 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, #e3f2fd 75%),
            linear-gradient(-45deg, transparent 75%, #e3f2fd 75%),
            #ffffff
          `,
          backgroundSize: '20px 20px, 20px 20px, 20px 20px, 20px 20px, 20px 20px, 20px 20px, 100% 100%',
          backgroundPosition: '0 0, 0 0, 0 0, 0 10px, 10px -10px, -10px 0px, 0 0'
        }}
      >
        <div className="text-center w-100 mb-3">
          <h4 className="text-muted mb-0">All Time Payment Analytics</h4>
          <small className="text-muted">Historical payment performance overview</small>
        </div>

        {/* All Time Payments Done */}
        <div className="card shadow-sm text-center p-4 bg-white rounded position-relative overflow-hidden" style={{ minWidth: "200px" }}>
          <div className="position-absolute top-0 end-0 p-2">
            <div className="bg-success bg-opacity-10 rounded-circle p-2">
              <img src="https://img.icons8.com/?size=100&id=59805&format=png&color=28a745" alt="all payments done" style={{ width: "24px", height: "24px" }} />
            </div>
          </div>
          <h6 className="text-muted mb-3 mt-2">Total Completed</h6>
          <div className="d-flex justify-content-center align-items-center">
            <p className="mb-0 display-4 fw-bold text-success">
              {dashboardReports?.all_payment_data?.all_payment_done_count || 0}
            </p>
          </div>
          <small className="text-success fw-semibold">📈 Lifetime success</small>
        </div>

        {/* All Time Payments Failed */}
        <div className="card shadow-sm text-center p-4 bg-white rounded position-relative overflow-hidden" style={{ minWidth: "200px" }}>
          <div className="position-absolute top-0 end-0 p-2">
            <div className="bg-danger bg-opacity-10 rounded-circle p-2">
              <img src="https://img.icons8.com/?size=100&id=59805&format=png&color=dc3545" alt="all payments failed" style={{ width: "24px", height: "24px" }} />
            </div>
          </div>
          <h6 className="text-muted mb-3 mt-2">Total Failed</h6>
          <div className="d-flex justify-content-center align-items-center">
            <p className="mb-0 display-4 fw-bold text-danger">
              {dashboardReports?.all_payment_data?.all_payment_failed_count || 0}
            </p>
          </div>
          <small className="text-danger fw-semibold">📉 Failed attempts</small>
        </div>

        {/* All Time Payments Pending */}
        <div className="card shadow-sm text-center p-4 bg-white rounded position-relative overflow-hidden" style={{ minWidth: "200px" }}>
          <div className="position-absolute top-0 end-0 p-2">
            <div className="bg-warning bg-opacity-10 rounded-circle p-2">
              <img src="https://img.icons8.com/?size=100&id=59805&format=png&color=ffc107" alt="all payments pending" style={{ width: "24px", height: "24px" }} />
            </div>
          </div>
          <h6 className="text-muted mb-3 mt-2">Total Pending</h6>
          <div className="d-flex justify-content-center align-items-center">
            <p className="mb-0 display-4 fw-bold text-warning">
              {dashboardReports?.all_payment_data?.all_payment_pending_count || 0}
            </p>
          </div>
          <small className="text-warning fw-semibold">⏳ Awaiting processing</small>
        </div>
      </motion.div>

{/*  Overall & Monthly Cashflow Donut Charts */}
<motion.div
  className="d-flex justify-content-center my-4 gap-4 flex-wrap"
  initial={{ opacity: 0, y: 40 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.7, delay: 1.2 }}
>
  {/* Overall Cashflow */}
  <div
    className="p-4 bg-white rounded"
    style={{ minWidth: "350px", flex: "1 1 350px", maxWidth: "450px" }}
  >
    <h5 className="text-center mb-4 text-muted">Overall Cashflow & Profit</h5>

    {dashboardReports ? (
      <>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={[
                {
                  name: "Cash Flow",
                  value: Number(dashboardReports?.all_time_cash_payment?.total_amount?.toFixed(2)) || 1,
                },
                {
                  name: "Total Profit",
                  value: Number(dashboardReports?.all_time_cash_payment?.total_net_profit?.toFixed(2)) || 1,
                },
              ]}
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={100}
              paddingAngle={3}
              dataKey="value"
            >
              <Cell fill="#28a765" /> {/* Inflow */}
              <Cell fill="#dc3545" /> {/* Outflow */}
            </Pie>
            <Tooltip formatter={(v, n) => [`$${v.toFixed(2)}`, n]} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>

        {/* 💬 Summary below chart */}
        {/* <div className="mt-4 pt-3 border-top">
          <div className="d-flex justify-content-between">
            <span className="fw-semibold text-success">💰 Cash Flow</span>
            <span className="fw-bold text-success">
              ${dashboardReports?.all_time_cash_payment?.total_amount?.toFixed(2)}
              
            </span>
          </div>

          <div className="d-flex justify-content-between mt-2 gap-2">
            <span className="fw-semibold text-danger">💸 Total Profit </span>
            <span className="fw-bold text-danger">
              
              ${dashboardReports?.all_time_cash_payment?.total_net_profit?.toFixed(2)}
            </span>
          </div>

        </div> */}
      </>
    ) : (
      <div className="text-center text-muted py-4">
        <p>No cashflow data available</p>
      </div>
    )}
  </div>

  {/* 📆 This Month’s Cashflow */}
  <div
    className=" p-4 bg-white rounded"
    style={{ minWidth: "350px", flex: "1 1 350px", maxWidth: "450px" }}
  >
    <h5 className="text-center mb-4 text-muted">
      This Month’s Cashflow & Profit
    </h5>

    {dashboardReports ? (
      <>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={[
                {
                  name: "Cash Flow",
                  
                   value: Number(dashboardReports?.this_month_cash_payment?.total_amount?.toFixed(2)) || 1,
                },
                {
                  name: "Total Profit",
                  value: Number(dashboardReports?.this_month_cash_payment?.total_net_profit?.toFixed(2)) || 1,

                   
                },
              ]}
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={100}
              paddingAngle={3}
              dataKey="value"
            >
              <Cell fill="#007bff" /> {/* Monthly Inflow */}
              <Cell fill="#fd7e14" /> {/* Monthly Outflow */}
            </Pie>
            <Tooltip formatter={(v, n) => [`$${v.toFixed(2)}`, n]} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>

        {/* 💬 Summary below chart */}
        {/* <div className="mt-4 pt-3 border-top">
          <div className="d-flex justify-content-between">
            <span className="fw-semibold text-primary">💰 Cash Flow</span>
            <span className="fw-bold text-primary">
              ${dashboardReports?.this_month_cash_payment?.total_amount?.toFixed(2)}
            </span>
          </div>

          <div className="d-flex justify-content-between mt-2">
            <span className="fw-semibold text-warning">💸 Total Profit</span>
            <span className="fw-bold text-warning">
             ${dashboardReports?.this_month_cash_payment?.total_net_profit?.toFixed(2)}

            </span>
          </div>
        </div> */}
      </>
    ) : (
      <div className="text-center text-muted py-4">
        <p>No monthly data available</p>
      </div>
    )}
  </div>
</motion.div>

      {/* Money Received & Payment Breakdown Section */}
      <motion.div 
        className="d-flex justify-content-center my-4 gap-4 flex-wrap"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.9 }}
      >
        {/* Money Received Card */}
        <motion.div 
          className="card shadow-sm"
          style={{ minWidth: "300px", flex: "1 1 300px", maxWidth: "400px" ,background: "linear-gradient(135deg, #0564bf, #89CFF0)"}}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          >
          <div className="card-body text-center px-4" 
          // style={{background: "linear-gradient(135deg, #28a745, #20c997)"}}
          >
            <div className="d-flex justify-content-center align-items-center mb-3">
              <div className="bg-whi bg-opacity-20 rounded-circle p-3 me-3">
                <img src="https://img.icons8.com/?size=100&id=hmpoVACZBCDl&format=png&color=000000" alt="money received"  />
              </div>
              <div>
                <h3 className="text-white mb-1">Money Received</h3>
                <small className="text-light">Total revenue</small>
              </div>
            </div>
            <div className="display-3 fw-bold text-white mb-2">
              ${dashboardReports?.money_received || 0}
            </div>
            <div className="text-light">
              <small>💵 SGD Currency</small>
            </div>
          </div>
        </motion.div>
        

        {/* Payment Breakdown Chart */}
        <motion.div 
          className="card shadow-sm"
          style={{ minWidth: "300px", flex: "1 1 300px" }}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 1.1 }}
        >
          <div className="card-body p-4">
            <h5 className="text-center mb-4 text-muted">Payment Fee Breakdown</h5>
            {paymentBreakdownData.length > 0 ? (
              <div className="d-flex flex-column flex-md-row align-items-center gap-4">
                {/* Pie Chart */}
                <div style={{ flex: "1", minWidth: "400px" }}>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={paymentBreakdownData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={60}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {paymentBreakdownData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={index === 0 ? '#28a745' : index === 1 ? '#17a2b8' : '#6f42c1'}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`$${value}`, 'Amount']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Legend and Details */}
                <div style={{ flex: "1", minWidth: "200px" }}>
                  <div className="d-flex flex-column gap-3">
                    {paymentBreakdownData.map((entry, index) => (
                      <div key={entry.name} className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <div 
                            className="me-3 rounded-circle" 
                            style={{ 
                              width: "16px", 
                              height: "16px",
                              backgroundColor: index === 0 ? '#28a745' : index === 1 ? '#17a2b8' : '#6f42c1'
                            }}
                          ></div>
                          <span className="fw-semibold text-muted">{entry.name}</span>
                        </div>
                        <span className="fw-bold text-success">${entry.amount}</span>
                      </div>
                    ))}
                    
                    {/* Total Amount */}
                    <div className="mt-3 pt-3 border-top">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-semibold text-muted">Total Amount:</span>
                        <span className="fw-bold text-primary ">
                          ${dashboardReports?.payment_breakdown?.amount || 0}
                        </span>
                      </div>
                    </div>
                    
                    {/* Total Fees */}
                    <div className="pt-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-semibold text-muted">Inclusive Fee:</span>
                        <span className="fw-bold text-warning">
                          ${paymentBreakdownData.reduce((sum, item) => sum + item.amount, 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-muted py-4">
                <p>No payment breakdown data available</p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>


    </motion.div>
    </div>
    <DashboardFooter/>
    </div>
  );
};

export default DashboardReports;
